import { formatDate } from "date-fns/format";
import { Spinner } from "@radix-ui/themes";
import { cn } from "@/core/helpers/cn";
import copyText from "@/core/helpers/copyText";
import formatPrice from "@/core/helpers/formatPrice";
import { CopyIcon, Document5Icon } from "@/shared/icons";
import { FEE_LIMIT } from "@/domains/bank/constants";
import toast from "@/domains/toasts/helpers/toast";
import { useBankCharacterQuery } from "@/domains/bank/hooks/useBankCharacterQuery";
import { BankBranchCardInfoContainer } from "@/domains/bank/containers/branch/BankBranchCardInfoContainer";

// Return last 8 digits of the account number with space between every 4 digits
function getAccountNumberPreview(accountNumber: string) {
  return (
    accountNumber
      .slice(-8)
      .match(/.{1,4}/g)
      ?.join(" ") || ""
  );
}

function SectionTitle({ title }: { title: string }) {
  return <h3 className="text-12 uppercase text-white/50">{title}</h3>;
}

function ContentCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("bg-dark-800 rounded-[14px] p-6", className)}>{children}</div>;
}

function TariffInfoCard({
  value,
  title,
  feePercent,
  feeLimitAmount,
}: {
  value: number;
  title: string;
  feePercent: number;
  feeLimitAmount: number;
}) {
  return (
    <ContentCard className="flex flex-col gap-2">
      <h4 className="text-16 font-bold leading-none">до {formatPrice(value)} ₽</h4>
      <p className="text-11 uppercase text-white/50 leading-none mb-6">{title}</p>

      <div className="bg-[#21a0381a] text-14 text-sbank rounded-xl p-2 w-fit">{feePercent}%</div>

      <div>
        <p className="text-15 text-white/70">Комиссия за последующие операции</p>
        <p className="text-14 font-normal text-white/30">
          * Но не более <span className="text-white/60">{formatPrice(feeLimitAmount)} ₽</span> за
          одну операцию
        </p>
      </div>
    </ContentCard>
  );
}

export function BankBranchTariffTabContainer() {
  const { data, isLoading } = useBankCharacterQuery();

  const tariffName = data?.tariff.name;

  return (
    <div className="grid grid-cols-[360px_1fr] gap-7 h-full">
      <BankBranchCardInfoContainer />

      {isLoading ? (
        <Spinner className="size-5 animate-spin" />
      ) : (
        <div className="flex flex-col gap-6 h-full">
          <div className="flex flex-col gap-4">
            <h3 className="text-18 font-bold">{tariffName} тариф</h3>
            <div className="grid grid-cols-2 gap-3">
              <TariffInfoCard
                value={data.tariff.withdrawalLimit}
                title="Снятие средств без комиссии"
                feePercent={data.tariff.withdrawalFeePercent}
                feeLimitAmount={FEE_LIMIT}
              />

              <TariffInfoCard
                value={data.tariff.transferLimit}
                title="Переводы без комиссии"
                feePercent={data.tariff.transferFeePercent}
                feeLimitAmount={FEE_LIMIT}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle title="Номер банковского счета" />
            <ContentCard className="p-5">
              <div className="flex text-15 gap-2 text-white/70">
                <div className="flex items-center gap-2 text-sbank">
                  <Document5Icon className="size-5" />
                  Номер счета:
                </div>

                <div className="text-16 text-white">
                  {getAccountNumberPreview(data.accountNumber)}
                </div>

                <button
                  type="button"
                  className="ml-auto text-white/30 hover:text-white/60 transition-colors duration-200"
                  onClick={() => {
                    copyText(data.accountNumber);
                    toast.success("Номер счета скопирован в буфер обмена");
                  }}
                >
                  <CopyIcon className="size-5" />
                </button>
              </div>
            </ContentCard>
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle title="Обслуживание" />
            <ContentCard className="flex justify-between p-5">
              <div className="text-16 font-bold text-sbank">{formatPrice(data.tariff.price)} ₽</div>
              <p className="text-15 text-white/70 font-normal">
                <span className="text-white/30">Ежемесячный платёж:</span>{" "}
                {formatDate(data.nextTariffPayment, "dd.MM.yyyy")}
              </p>
            </ContentCard>
          </div>

          <p className="text-15 text-white/30 font-normal mt-auto">
            * Если вы постоянно выходите за рамки заданных лимитов текущего тарифа, советуем вам
            сменить его на более подходящий в разделе смены тарифа.
          </p>
        </div>
      )}
    </div>
  );
}
